import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { categoryKey, categoryStatuses, loadingStatuses, combinedFileCategoryStatuses, separateFileCategoryStatuses } from '../../util/categoryConsts'
import { FlexColumn, FlexEndContainer, FlexRow, SpaceBetweenRowContainer, WhiteBGContainer } from '../styles/Containers'
import { BoldFormTitle } from '../styles/Text'
import CategoryStatus from './CategoryStatus'
import CategoryOriginalFileTable from './CategoryOriginalFileTable'
import RedactionFileList from './RedactionFileList'
import CategoryActionButton from './CategoryActionButton'
import CombineCategoryButton from './CombineCategoryButton'
import DeduplicationButton from '../deduplication/DeduplicationButton'
import Tooltip from '../common/Tooltip'
import CategoryRemovedFilesTable from './CategoryRemovedFilesTable'
import MoveFilesToCategoryDialog from './MoveFilesToCategoryDialog'

const CenteredAlignedRow = styled(FlexRow)`
  align-items: center;
`

// Files can be reordered after conversion and before combining.
const reorderableCategoryStatuses = ['CONVERTED', 'DUPLICATES_READY']

// Files can be multi-selected while the category holds separate, settled files.
const selectableCategoryStatuses = ['RAW', 'CONVERTED', 'DUPLICATES_READY']

// Client-only display name for archived files.
const REMOVED_FILES_CATEGORY_NAME = 'Removed Files'
const RESPONSE_FILES_INFO =
  'Only Redactions Finalized & Redactions Summary will be added to your response. ' +
  'Once added, files can only be viewed or downloaded. Editing options return when the files are removed from the response.'

function CategoryFileTable({
  category,
  foiId,
  categories = [],
  // Preserve undefined so the preview can distinguish loading from removal.
  files,
  preview = null,
  onPreviewConsumed,
  duplicateGroups = [],
  disabled = false,
  handleRemoveFile = () => {},
  handleRestoreFile = () => {},
  handleUpdateWorkflow = () => {},
  handleAddRemoveResponseFiles = () => {},
  handleUploadFiles = () => {},
  handleRenameFile = () => {}
}) {
  // {mode, files} while the move/restore dialog is open.
  const [moveDialog, setMoveDialog] = useState()

  // The current category is never a destination.
  const destinationCategories = categories.filter(c => c.id !== category.id)

  function handleMoved({ categoryId }) {
    // Partial successes are applied too — refresh either way.
    if (moveDialog?.mode === 'restore') handleRestoreFile(categoryId)
    else handleRemoveFile(categoryId)
  }

  return (
    <FlexColumn gap="15px">
      <WhiteBGContainer padding="20px">
        <SpaceBetweenRowContainer>
          <CenteredAlignedRow>
            <BoldFormTitle>{category.name}</BoldFormTitle>
            {category.name !== REMOVED_FILES_CATEGORY_NAME && <CategoryStatus status={category.status} />}
          </CenteredAlignedRow>
          {!disabled && (
            <FlexEndContainer>
              {(() => {
                switch (category.status) {
                  case 'RAW':
                    return <CategoryActionButton id={category.id} type="convert" onSuccess={handleUpdateWorkflow} />
                  case 'CONVERTED':
                    return <CategoryActionButton id={category.id} type="findDuplicates" onSuccess={handleUpdateWorkflow} />
                  case 'DUPLICATES_READY':
                    return (
                      <CenteredAlignedRow gap="15px">
                        <DeduplicationButton categoryId={category.id} duplicateGroups={duplicateGroups} onClose={handleRemoveFile} />
                        <CombineCategoryButton categoryId={category.id} onSuccess={handleUpdateWorkflow} />
                      </CenteredAlignedRow>
                    )
                  case 'COMBINED':
                    return <CategoryActionButton id={category.id} type="suggestRedactions" onSuccess={handleUpdateWorkflow} />
                  case 'REDACTIONS_NOTED':
                    return <CategoryActionButton id={category.id} type="applyRedactions" onSuccess={handleUpdateWorkflow} />
                  case 'REDACTIONS_FINALIZED':
                    return (
                      <CenteredAlignedRow>
                        <CategoryActionButton id={category.id} type="sendToResponse" onSuccess={handleAddRemoveResponseFiles} />
                        <Tooltip info={RESPONSE_FILES_INFO} />
                      </CenteredAlignedRow>
                    )
                  case 'ADDED_TO_RESPONSE':
                    return <CategoryActionButton id={category.id} type="removeFromResponse" onSuccess={handleAddRemoveResponseFiles} />
                  case 'CONVERTING':
                  case 'DETERMINING_DUPLICATES':
                  case 'COMBINING_FILES':
                  case 'SUGGESTING_REDACTIONS':
                  case 'SAVING_REDACTIONS':
                  case 'FINALIZING_REDACTIONS':
                    return (
                      <CategoryActionButton
                        id={category.pendingWorkflowId}
                        type="cancel"
                        onSuccess={handleUpdateWorkflow}
                        additionalButtonText={` ${categoryKey[category.status].label}`}
                      />
                    )
                  default:
                    return <></>
                }
              })()}
            </FlexEndContainer>
          )}
        </SpaceBetweenRowContainer>
      </WhiteBGContainer>
      {category.name === REMOVED_FILES_CATEGORY_NAME && (
        <CategoryRemovedFilesTable
          files={files}
          foiId={foiId}
          categories={categories}
          preview={preview}
          onPreviewConsumed={onPreviewConsumed}
          selectable
          disabled={loadingStatuses.includes(category.status) || disabled}
          handleRestoreFile={handleRestoreFile}
          onRestoreSelected={files => setMoveDialog({ mode: 'restore', files })}
        />
      )}
      {separateFileCategoryStatuses.includes(category.status) && (
        <CategoryOriginalFileTable
          files={files}
          foiId={foiId}
          fileOrder={category.fileOrder}
          categoryId={category.id}
          preview={preview}
          onPreviewConsumed={onPreviewConsumed}
          reorderable={reorderableCategoryStatuses.includes(category.status)}
          renameable={true}
          selectable={selectableCategoryStatuses.includes(category.status)}
          disabled={loadingStatuses.includes(category.status) || disabled}
          handleRemoveFile={handleRemoveFile}
          handleRenameFile={handleRenameFile}
          onMoveSelected={files => setMoveDialog({ mode: 'move', files })}
        />
      )}
      {combinedFileCategoryStatuses.includes(category.status) && (
        <RedactionFileList
          files={files}
          foiId={foiId}
          preview={preview}
          onPreviewConsumed={onPreviewConsumed}
          loading={loadingStatuses.includes(category.status) || disabled}
          viewOnly={category.status === 'ADDED_TO_RESPONSE'}
          handleReplaceFile={handleUploadFiles}
          handleRenameFile={handleRenameFile}
        />
      )}
      <MoveFilesToCategoryDialog
        open={!!moveDialog}
        mode={moveDialog?.mode ?? 'move'}
        foiId={foiId}
        categories={destinationCategories}
        files={moveDialog?.files ?? []}
        onClose={() => setMoveDialog(undefined)}
        onMoved={handleMoved}
      />
    </FlexColumn>
  )
}

CategoryFileTable.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(categoryStatuses),
    pendingWorkflowId: PropTypes.string
  }).isRequired,
  foiId: PropTypes.string.isRequired,
  categories: PropTypes.array,
  files: PropTypes.array,
  preview: PropTypes.shape({
    fileId: PropTypes.string.isRequired,
    filename: PropTypes.string,
    page: PropTypes.number,
    nonce: PropTypes.number
  }),
  onPreviewConsumed: PropTypes.func,
  duplicateGroups: PropTypes.array,
  disabled: PropTypes.bool,
  handleRemoveFile: PropTypes.func,
  handleRestoreFile: PropTypes.func,
  handleUpdateWorkflow: PropTypes.func,
  handleAddRemoveResponseFiles: PropTypes.func,
  handleUploadFiles: PropTypes.func,
  handleRenameFile: PropTypes.func
}
export default CategoryFileTable
